// components/ProductSearchSelect.jsx
import React, { useEffect, useRef, useState } from "react";

/**
 * Props:
 * - products: array of { id, label, price, img, weight, meta }
 * - value: current product id
 * - onChange: (productId) => void
 * - placeholder: string
 */
export default function ProductSearchSelect({
  products = [],
  value,
  onChange,
  placeholder = "Search product...",
}) {
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [filtered, setFiltered] = useState(products);
  const [highlight, setHighlight] = useState(-1); // index of highlighted result

  // sync filtered when products or query changes
  useEffect(() => {
    const q = query.trim().toLowerCase();
    const f = q
      ? products.filter(
          (p) =>
            (p.label || "").toLowerCase().includes(q) ||
            (p.weight || "").toLowerCase().includes(q) ||
            (p.meta || "").toLowerCase().includes(q)
        )
      : products.slice();
    setFiltered(f);
    setHighlight(f.length ? 0 : -1);
  }, [products, query]);

  // close on outside click
  useEffect(() => {
    function handleOutside(e) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleOutside);
    return () => document.removeEventListener("mousedown", handleOutside);
  }, []);

  // selected product object
  const selected = products.find((p) => p.id === value);

  // keyboard handlers on input
  const onInputKeyDown = (e) => {
    if (!open && (e.key === "ArrowDown" || e.key === "ArrowUp")) {
      setOpen(true);
      return;
    }
    if (open) {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setHighlight((h) => Math.min(h + 1, filtered.length - 1));
        scrollToIndex(highlight + 1);
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setHighlight((h) => Math.max(h - 1, 0));
        scrollToIndex(highlight - 1);
      } else if (e.key === "Enter") {
        e.preventDefault();
        if (filtered[highlight]) {
          handleSelect(filtered[highlight]);
        }
      } else if (e.key === "Escape") {
        setOpen(false);
      }
    }
  };

  const scrollToIndex = (index) => {
    if (!listRef.current) return;
    const el = listRef.current.children[index];
    if (el) {
      const parent = listRef.current;
      const elTop = el.offsetTop;
      const elBottom = elTop + el.offsetHeight;
      if (elTop < parent.scrollTop) parent.scrollTop = elTop;
      else if (elBottom > parent.scrollTop + parent.clientHeight)
        parent.scrollTop = elBottom - parent.clientHeight;
    }
  };

  const handleSelect = (p) => {
    onChange && onChange(p.id);
    setQuery("");
    setOpen(false);
    inputRef.current?.blur();
  };

  const handleClear = (e) => {
    e?.preventDefault();
    setQuery("");
    inputRef.current?.focus();
    setOpen(true);
  };

  return (
    <div
      ref={wrapperRef}
      className="position-relative"
      style={{ minWidth: 240 }}
    >
      <div className="d-flex align-items-center gap-2">
        <input
          ref={inputRef}
          type="text"
          className="form-control form-control-sm"
          placeholder={placeholder}
          aria-expanded={open}
          aria-haspopup="listbox"
          value={open ? query : selected ? selected.label : query}
          onFocus={() => {
            setOpen(true);
            setQuery("");
          }}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          onKeyDown={onInputKeyDown}
        />

        <button
          className="btn btn-outline-secondary btn-sm"
          type="button"
          onClick={() => {
            if (open) {
              setOpen(false);
            } else {
              setOpen(true);
              setTimeout(() => inputRef.current?.focus(), 0);
            }
          }}
          aria-label="Toggle products"
        >
          ▾
        </button>
      </div>

      {open && (
        <div
          className="card position-absolute mt-1 w-100 shadow-sm"
          style={{ zIndex: 1600, maxHeight: 320, overflow: "hidden" }}
        >
          <div
            className="d-flex justify-content-between align-items-center px-2 py-2 border-bottom"
            style={{ background: "#fff" }}
          >
            <div className="small text-muted">
              {filtered.length} result{filtered.length !== 1 ? "s" : ""}
            </div>
            <div>
              <button
                className="btn btn-sm btn-link text-decoration-none"
                onClick={handleClear}
              >
                Clear
              </button>
            </div>
          </div>

          <div
            ref={listRef}
            role="listbox"
            tabIndex={-1}
            style={{ maxHeight: 260, overflow: "auto" }}
          >
            {filtered.length === 0 && (
              <div className="p-3 text-center small text-muted">
                No products found
              </div>
            )}

            {filtered.map((p, i) => {
              const isHighlighted = i === highlight;
              return (
                <div
                  key={p.id}
                  role="option"
                  aria-selected={selected?.id === p.id}
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleSelect(p);
                    if (e.key === "ArrowDown") {
                      e.preventDefault();
                      setHighlight((h) => Math.min(h + 1, filtered.length - 1));
                      scrollToIndex(i + 1);
                    }
                    if (e.key === "ArrowUp") {
                      e.preventDefault();
                      setHighlight((h) => Math.max(h - 1, 0));
                      scrollToIndex(i - 1);
                    }
                    if (e.key === "Escape") setOpen(false);
                  }}
                  onClick={() => handleSelect(p)}
                  onMouseEnter={() => setHighlight(i)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                    padding: "10px 12px",
                    cursor: "pointer",
                    background: isHighlighted ? "#f5f7fb" : "#fff",
                    borderBottom: "1px solid #eee",
                  }}
                >
                  <img
                    src={p.img}
                    alt={p.label}
                    style={{
                      width: 46,
                      height: 46,
                      objectFit: "cover",
                      borderRadius: 6,
                      flex: "0 0 46px",
                    }}
                  />

                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        fontSize: 14,
                        fontWeight: 600,
                        color: "#222",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {p.label}
                    </div>
                    <div
                      style={{ fontSize: 12, color: "#7b7b7b", marginTop: 4 }}
                    >
                      {p.meta ? p.meta + " • " : ""}
                      {p.weight || ""}
                    </div>
                  </div>

                  <div
                    style={{
                      marginLeft: 8,
                      textAlign: "right",
                      color: "#6b6b6b",
                      fontSize: 13,
                    }}
                  >
                    ₹{(p.price || 0).toFixed(2)}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
